/**
 * sl-opil.7.1: React hooks for managed AI status and mutations.
 */
import { useMutation } from '@affine/core/components/hooks/use-mutation';
import { useQuery } from '@affine/core/components/hooks/use-query';
import {
  cancelCreditSubscriptionMutation,
  createCreditCheckoutMutation,
  managedAiStatusQuery,
  type WorkspaceCreditSubscriptionsQuery,
  workspaceCreditSubscriptionsQuery,
} from '@affine/graphql';
import { useCallback } from 'react';

const NON_BLOCKING_QUERY = {
  suspense: false,
  shouldRetryOnError: false,
} as const;

export function useManagedAIStatus(
  workspaceId: string,
  workspaceFlavour?: string
) {
  const isLocal = workspaceFlavour === 'local';
  const { data, isLoading, error, mutate } = useQuery(
    isLocal
      ? undefined
      : { query: managedAiStatusQuery, variables: { workspaceId } },
    NON_BLOCKING_QUERY
  );

  return {
    status: isLocal ? null : (data?.managedAIStatus ?? null),
    isLoading: isLocal ? false : isLoading,
    error: isLocal ? null : error,
    refresh: mutate,
  };
}

export function useWorkspaceCreditSubscriptions(
  workspaceId: string,
  workspaceFlavour?: string
) {
  const isLocal = workspaceFlavour === 'local';
  const { data, isLoading, error, mutate } = useQuery(
    isLocal
      ? undefined
      : {
          query: workspaceCreditSubscriptionsQuery,
          variables: { workspaceId },
        },
    NON_BLOCKING_QUERY
  );

  const subscriptions = (
    isLocal ? [] : (data?.workspaceCreditSubscriptions ?? [])
  ) as WorkspaceCreditSubscriptionsQuery['workspaceCreditSubscriptions'];

  return {
    subscriptions,
    isLoading: isLocal ? false : isLoading,
    error: isLocal ? null : error,
    refresh: mutate,
  };
}

export function useCancelCreditSubscription(workspaceFlavour?: string) {
  const isLocal = workspaceFlavour === 'local';
  const { trigger, isMutating } = useMutation({
    mutation: cancelCreditSubscriptionMutation,
  });

  return {
    cancelSubscription: useCallback(
      (workspaceId: string, creditPackId: string) =>
        isLocal
          ? Promise.resolve(null)
          : trigger({ workspaceId, creditPackId }),
      [trigger, isLocal]
    ),
    isLoading: isLocal ? false : isMutating,
  };
}

export function useCreateCreditCheckout(workspaceFlavour?: string) {
  const isLocal = workspaceFlavour === 'local';
  const { trigger, isMutating } = useMutation({
    mutation: createCreditCheckoutMutation,
  });

  return {
    createCheckout: useCallback(
      (
        workspaceId: string,
        packId: string,
        successUrl: string,
        cancelUrl: string,
        withdrawalConsent = true
      ) =>
        isLocal
          ? Promise.resolve(null)
          : trigger({
              workspaceId,
              packId,
              successUrl,
              cancelUrl,
              withdrawalConsent,
            }),
      [trigger, isLocal]
    ),
    isLoading: isLocal ? false : isMutating,
  };
}
